// src/pages/TemplatePreview.tsx
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { RecipientFlow } from "../components/recipient/RecipientFlow";
import { CARD_TEMPLATES } from "../constants/templates";
import { CardData } from "../types/card.types";

// Helper to convert template to preview card
function templateToPreviewCard(templateId: string, template: any): CardData {
  const now = new Date().toISOString();
  const archetype = template.archetype || "heartbeat";

  return {
    id: `preview-${templateId}`,
    shareableLink: `/preview/template/${templateId}`,
    senderId: "preview",
    senderEmail: "preview@lrnsy.com",
    senderName: "Lrnsy",
    recipientName: "Beautiful Soul",
    recipientType: template.recipientType,
    recipientImage: null,
    theme: template.theme.id,
    title: template.preview.title,
    message: template.preview.message,
    personalNote: template.preview.personalNote,
    primaryColor: template.theme.primaryColor,
    secondaryColor: template.theme.secondaryColor,
    accentColor: template.theme.accentColor,
    fontFamily: template.theme.fontFamily,
    includeBouquet: true,
    bouquetAmount: 5000, // Default preview amount
    petals: 5,
    includeHeartParticles: false,
    includeSparkles: false,
    status: "paid",
    paymentRef: null,
    createdAt: now,
    updatedAt: now,
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    viewedAt: null,
    acceptedAt: null,
    archetype,
    privacyMode: "standard_private",
    accessStatus: "active",
    encryptedMessage: null,
    encryptedPersonalNote: null,
    encryptedMemory: null,
    encryptedInsideJoke: null,
    encryptionVersion: null,
    encryptionSalt: null,
    encryptionIv: null,
    encryptedKey: null,
    keyWrapVersion: null,
    requiresPasscode: false,
    deleteAfterOpen: false,
    openedCount: 0,
    lastOpenedAt: null,
  };
}

export default function TemplatePreview() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [previewCard, setPreviewCard] = useState<CardData | null>(null);

  useEffect(() => {
    if (templateId && CARD_TEMPLATES[templateId as keyof typeof CARD_TEMPLATES]) {
      const template = CARD_TEMPLATES[templateId as keyof typeof CARD_TEMPLATES];
      setPreviewCard(templateToPreviewCard(templateId, template));
    } else {
      navigate("/recipient/preview");
    }
  }, [templateId, navigate]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleCustomize = () => {
    if (templateId) {
      navigate(`/create?template=${templateId}`);
    }
  };

  if (!previewCard) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading template...</div>
      </div>
    );
  }

  const template = CARD_TEMPLATES[templateId as keyof typeof CARD_TEMPLATES];

  return (
    <div className="relative">
      <RecipientFlow card={previewCard} />

      {/* Preview Controls */}
      <div className="fixed top-4 left-4 right-4 z-50 flex justify-between items-center pointer-events-none">
        <button
          onClick={handleBack}
          className="pointer-events-auto bg-black/50 backdrop-blur-xl text-white px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2 hover:bg-black/70 transition-colors"
        >
          <ArrowLeft size={16} />
          Back
        </button>

        <div className="pointer-events-auto bg-black/50 backdrop-blur-xl text-white px-4 py-2 rounded-full text-sm font-bold">
          {template?.name} Preview
        </div>
      </div>

      {/* Customize This Template CTA */}
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 2 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-t from-black via-black/95 to-transparent"
      >
        <div className="max-w-md mx-auto">
          <button
            onClick={handleCustomize}
            className="w-full group relative overflow-hidden bg-white text-black px-6 py-4 rounded-2xl font-bold flex items-center justify-between"
          >
            <span className="text-sm uppercase tracking-wider">
              Customize This {template?.name}
            </span>
            <motion.span
              animate={{ x: [0, 5, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
            >
              <ArrowRight size={20} />
            </motion.span>
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-black/5 to-transparent"
              animate={{ x: ["-100%", "100%"] }}
              transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
            />
          </button>
          <p className="text-center text-xs text-white/50 mt-2">
            Like what you see? Make it personal.
          </p>
        </div>
      </motion.div>
    </div>
  );
}
